/**
 * Trade-In Modal JavaScript
 * Handles the multi-step trade-in flow, API communication, and cart integration
 */

(function() {
  'use strict';

  const TradeIn = {
    config: null,
    data: null,
    state: {
      currentStep: 1,
      selectedBrand: null,
      selectedModel: null,
      selectedCondition: null,
      tradeInValue: 0,
      discountCode: null,
      isLoading: false,
    },

    elements: {},

    init() {
      this.config = window.TradeInConfig || {};
      this.cacheElements();
      this.bindEvents();
      this.loadConfig();
      this.monitorCartForTradeIn();
    },

    cacheElements() {
      this.elements = {
        trigger: document.getElementById('trade-in-trigger'),
        modal: document.getElementById('trade-in-modal'),
        closeBtn: document.getElementById('trade-in-close'),
        steps: document.querySelectorAll('.trade-in-step'),
        stepDots: document.querySelectorAll('.trade-in-step-dot'),
        brandOptions: document.getElementById('brand-options'),
        modelOptions: document.getElementById('model-options'),
        conditionOptions: document.getElementById('condition-options'),
        selectedBrandDisplay: document.getElementById('selected-brand-display'),
        selectedModelDisplay: document.getElementById('selected-model-display'),
        summaryBrand: document.getElementById('summary-brand'),
        summaryModel: document.getElementById('summary-model'),
        summaryCondition: document.getElementById('summary-condition'),
        summaryValue: document.getElementById('summary-value'),
        preorderMessage: document.getElementById('preorder-message'),
        termsMessage: document.getElementById('terms-message'),
        termsCheckbox: document.getElementById('trade-in-terms-checkbox'),
        confirmBtn: document.getElementById('trade-in-confirm'),
        doneBtn: document.getElementById('trade-in-done'),
        retryBtn: document.getElementById('trade-in-retry'),
        discountCodeDisplay: document.getElementById('discount-code-display'),
        successMessage: document.getElementById('success-message'),
        errorMessage: document.getElementById('error-message'),
        backBtns: document.querySelectorAll('.trade-in-back-btn'),
      };
    },

    bindEvents() {
      // Modal open/close
      this.elements.trigger?.addEventListener('click', () => this.openModal());
      this.elements.closeBtn?.addEventListener('click', () => this.closeModal());
      this.elements.modal?.addEventListener('click', (e) => {
        if (e.target === this.elements.modal) this.closeModal();
      });

      // Keyboard navigation
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.elements.modal?.hidden) {
          this.closeModal();
        }
      });

      // Back buttons
      this.elements.backBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const targetStep = parseInt(btn.dataset.back);
          this.goToStep(targetStep);
        });
      });

      // Terms checkbox
      this.elements.termsCheckbox?.addEventListener('change', () => {
        this.elements.confirmBtn.disabled = !this.elements.termsCheckbox.checked;
      });

      // Confirm button
      this.elements.confirmBtn?.addEventListener('click', () => this.handleConfirm());

      // Done button
      this.elements.doneBtn?.addEventListener('click', () => this.closeModal());

      // Retry button
      this.elements.retryBtn?.addEventListener('click', () => this.goToStep(1));
    },

    async loadConfig() {
      try {
        const response = await fetch(`${this.config.apiEndpoint}/api/config`);
        if (!response.ok) throw new Error('Failed to load config');
        this.data = await response.json();
        this.renderBrands();
        this.updateMessages();
      } catch (error) {
        console.error('Trade-In: Failed to load config', error);
        this.elements.brandOptions.innerHTML = `
          <div class="trade-in-error-inline">
            Failed to load trade-in options. Please refresh the page.
          </div>
        `;
      }
    },

    updateMessages() {
      if (!this.data?.settings) return;
      
      const { settings } = this.data;
      
      if (settings.preorder_message) {
        const message = settings.preorder_message.replace(
          '{return_window_days}',
          settings.return_window_days || '14'
        );
        this.elements.preorderMessage.textContent = message;
      }
      
      if (settings.terms_message) {
        this.elements.termsMessage.textContent = settings.terms_message;
      }
    },

    openModal() {
      this.elements.modal.hidden = false;
      document.body.style.overflow = 'hidden';
      this.elements.modal.querySelector('.trade-in-modal')?.focus();
      this.resetState();
    },

    closeModal() {
      this.elements.modal.hidden = true;
      document.body.style.overflow = '';
      this.elements.trigger?.focus();
    },

    resetState() {
      this.state = {
        currentStep: 1,
        selectedBrand: null,
        selectedModel: null,
        selectedCondition: null,
        tradeInValue: 0,
        discountCode: null,
        isLoading: false,
      };
      this.goToStep(1);
      this.elements.termsCheckbox.checked = false;
      this.elements.confirmBtn.disabled = true;
    },

    goToStep(step) {
      this.state.currentStep = step;
      
      // Update step visibility - hide all, then show target
      this.elements.steps.forEach(el => {
        const stepNum = el.dataset.step;
        const isTarget = stepNum === step.toString();
        el.hidden = !isTarget;
        el.style.display = isTarget ? 'block' : 'none';
      });

      // Update dots
      this.elements.stepDots.forEach(dot => {
        const dotStep = parseInt(dot.dataset.step);
        dot.classList.toggle('active', dotStep <= step);
        dot.classList.toggle('completed', dotStep < step);
      });
    },

    showStep(stepName) {
      this.elements.steps.forEach(el => {
        const isTarget = el.dataset.step === stepName;
        el.hidden = !isTarget;
        el.style.display = isTarget ? 'block' : 'none';
      });
    },

    renderBrands() {
      if (!this.data?.brands) return;

      const html = this.data.brands.map(brand => `
        <button type="button" class="trade-in-option" data-value="${this.escapeHtml(brand.brand_name)}">
          ${this.escapeHtml(brand.brand_name)}
        </button>
      `).join('');

      this.elements.brandOptions.innerHTML = html;
      
      // Bind click events
      this.elements.brandOptions.querySelectorAll('.trade-in-option').forEach(btn => {
        btn.addEventListener('click', () => this.selectBrand(btn.dataset.value));
      });
    },

    selectBrand(brand) {
      this.state.selectedBrand = brand;
      this.elements.selectedBrandDisplay.textContent = brand;
      
      // Mark selected
      this.elements.brandOptions.querySelectorAll('.trade-in-option').forEach(btn => {
        btn.classList.toggle('selected', btn.dataset.value === brand);
      });

      // If "Other" selected, skip to condition with fixed offer
      if (brand === 'Other') {
        this.state.selectedModel = 'Other';
        this.renderConditions();
        this.goToStep(3);
      } else {
        this.renderModels(brand);
        this.goToStep(2);
      }
    },

    renderModels(brand) {
      if (!this.data?.phones) return;

      const models = this.data.phones.filter(p => p.brand === brand);
      
      if (models.length === 0) {
        this.elements.modelOptions.innerHTML = `
          <div class="trade-in-no-results">
            No models found for ${this.escapeHtml(brand)}. 
            <button type="button" class="trade-in-link-btn" id="select-other-from-models">Select "Other" instead</button>
          </div>
        `;
        document.getElementById('select-other-from-models')?.addEventListener('click', () => {
          this.selectBrand('Other');
        });
        return;
      }

      const html = models.map(phone => `
        <button type="button" class="trade-in-option" data-value="${this.escapeHtml(phone.model)}" data-base-value="${phone.base_trade_value}">
          <span class="trade-in-option-name">${this.escapeHtml(phone.model)}</span>
          <span class="trade-in-option-value">Up to $${phone.base_trade_value}</span>
        </button>
      `).join('');

      this.elements.modelOptions.innerHTML = html;

      // Bind click events
      this.elements.modelOptions.querySelectorAll('.trade-in-option').forEach(btn => {
        btn.addEventListener('click', () => this.selectModel(btn.dataset.value));
      });
    },

    selectModel(model) {
      this.state.selectedModel = model;
      this.elements.selectedModelDisplay.textContent = `${this.state.selectedBrand} ${model}`;
      
      // Mark selected
      this.elements.modelOptions.querySelectorAll('.trade-in-option').forEach(btn => {
        btn.classList.toggle('selected', btn.dataset.value === model);
      });

      this.renderConditions();
      this.goToStep(3);
    },

    renderConditions() {
      if (!this.data?.conditions) return;

      const html = this.data.conditions.map(condition => `
        <button type="button" class="trade-in-option trade-in-condition-option" data-value="${this.escapeHtml(condition.condition_name)}">
          <span class="trade-in-condition-name">${this.escapeHtml(condition.condition_name)}</span>
          <span class="trade-in-condition-modifier">${condition.percentage_modifier}% of value</span>
          <span class="trade-in-condition-desc">${this.escapeHtml(condition.description)}</span>
        </button>
      `).join('');

      this.elements.conditionOptions.innerHTML = html;

      // Bind click events
      this.elements.conditionOptions.querySelectorAll('.trade-in-option').forEach(btn => {
        btn.addEventListener('click', () => this.selectCondition(btn.dataset.value));
      });
    },

    async selectCondition(condition) {
      this.state.selectedCondition = condition;
      
      // Mark selected
      this.elements.conditionOptions.querySelectorAll('.trade-in-option').forEach(btn => {
        btn.classList.toggle('selected', btn.dataset.value === condition);
      });

      // Calculate trade-in value
      await this.calculateValue();
      
      // Update summary
      this.elements.summaryBrand.textContent = this.state.selectedBrand;
      this.elements.summaryModel.textContent = this.state.selectedModel;
      this.elements.summaryCondition.textContent = this.state.selectedCondition;
      this.elements.summaryValue.textContent = `$${this.state.tradeInValue}`;

      this.goToStep(4);
    },

    async calculateValue() {
      try {
        const response = await fetch(`${this.config.apiEndpoint}/api/calculate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brand: this.state.selectedBrand,
            model: this.state.selectedModel,
            condition: this.state.selectedCondition,
          }),
        });

        if (!response.ok) throw new Error('Failed to calculate value');
        
        const data = await response.json();
        this.state.tradeInValue = data.tradeInValue;
      } catch (error) {
        console.error('Trade-In: Failed to calculate value', error);
        // Fallback to client-side calculation
        this.calculateValueLocally();
      }
    },

    calculateValueLocally() {
      if (this.state.selectedBrand === 'Other') {
        this.state.tradeInValue = parseInt(this.data?.settings?.other_offer_amount) || 50;
        return;
      }

      const phone = this.data?.phones?.find(
        p => p.brand === this.state.selectedBrand && p.model === this.state.selectedModel
      );
      const condition = this.data?.conditions?.find(
        c => c.condition_name === this.state.selectedCondition
      );

      if (phone && condition) {
        const baseValue = parseFloat(phone.base_trade_value);
        const modifier = parseFloat(condition.percentage_modifier) / 100;
        this.state.tradeInValue = Math.round(baseValue * modifier);
      }
    },

    async handleConfirm() {
      if (this.state.isLoading) return;
      
      this.state.isLoading = true;
      this.setConfirmLoading(true);

      try {
        // Create discount code (product-specific)
        const response = await fetch(`${this.config.apiEndpoint}/api/create-discount`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tradeInValue: this.state.tradeInValue,
            brand: this.state.selectedBrand,
            model: this.state.selectedModel,
            condition: this.state.selectedCondition,
            productId: this.config.productId,
            variantId: this.config.variantId,
          }),
        });

        if (!response.ok) throw new Error('Failed to create discount');

        const data = await response.json();
        this.state.discountCode = data.discountCode;

        // Store discount code for tracking
        this.storeDiscountCode(data.discountCode);

        // Add to cart with trade-in properties
        await this.addToCart();

        // Show success
        this.elements.discountCodeDisplay.textContent = data.discountCode;
        if (data.message) {
          this.elements.successMessage.textContent = data.message;
        }
        this.showStep('success');

      } catch (error) {
        console.error('Trade-In: Failed to process', error);
        this.elements.errorMessage.textContent = 
          'We couldn\'t process your trade-in. Please try again or contact support.';
        this.showStep('error');
      } finally {
        this.state.isLoading = false;
        this.setConfirmLoading(false);
      }
    },

    async addToCart() {
      // Apply the discount code first
      await this.applyDiscountCode();

      // Find the native product form on the page
      const productForm = document.querySelector('form[action="/cart/add"]') || 
                          document.querySelector('[data-product-form]') ||
                          document.querySelector('product-form form') ||
                          document.querySelector('.product-form form');

      if (productForm) {
        console.log('Trade-In: Found native product form, using it');
        
        // Add our trade-in properties as hidden inputs
        const properties = {
          '_trade_in_brand': this.state.selectedBrand,
          '_trade_in_model': this.state.selectedModel,
          '_trade_in_condition': this.state.selectedCondition,
          '_trade_in_value': `$${this.state.tradeInValue}`,
          '_discount_code': this.state.discountCode,
        };

        // Remove any existing trade-in inputs
        productForm.querySelectorAll('input[name^="properties[_trade_in"]').forEach(el => el.remove());
        productForm.querySelectorAll('input[name^="properties[_discount"]').forEach(el => el.remove());

        // Add new hidden inputs for properties
        for (const [key, value] of Object.entries(properties)) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${key}]`;
          input.value = value;
          productForm.appendChild(input);
        }

        // Set quantity to 1
        const qtyInput = productForm.querySelector('input[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = '1';
        }

        // Find and click the Add to Cart button to trigger native behavior
        const addToCartBtn = productForm.querySelector('[type="submit"]') ||
                            productForm.querySelector('button[name="add"]') ||
                            productForm.querySelector('.add-to-cart-btn') ||
                            productForm.querySelector('[data-add-to-cart]');

        if (addToCartBtn) {
          console.log('Trade-In: Clicking native Add to Cart button');
          addToCartBtn.click();
          
          // Wait for the native cart update to complete
          await new Promise(resolve => setTimeout(resolve, 1500));
          return;
        }

        // Fallback: submit the form directly
        console.log('Trade-In: Submitting form directly');
        
        // Create a submit event
        const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
        productForm.dispatchEvent(submitEvent);
        
        if (!submitEvent.defaultPrevented) {
          // If form has ajax handling, it might prevent default
          // Give it time to process
          await new Promise(resolve => setTimeout(resolve, 1500));
        }
        
        return;
      }

      // Fallback to fetch API if no native form found
      console.log('Trade-In: No native form found, using fetch API');
      
      const formData = {
        id: this.config.variantId,
        quantity: 1,
        properties: {
          '_trade_in_brand': this.state.selectedBrand,
          '_trade_in_model': this.state.selectedModel,
          '_trade_in_condition': this.state.selectedCondition,
          '_trade_in_value': `$${this.state.tradeInValue}`,
          '_discount_code': this.state.discountCode,
        },
      };

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      // Trigger cart update events for theme integration
      await this.triggerCartUpdate();
    },

    async applyDiscountCode() {
      // Apply discount code using Shopify's cart update with discount
      // First, fetch via the discount URL to set the code in session
      try {
        await fetch('/discount/' + encodeURIComponent(this.state.discountCode), {
          method: 'GET',
          credentials: 'same-origin',
        });
      } catch (error) {
        console.log('Trade-In: Applied discount code via redirect fallback');
      }
    },

    async triggerCartUpdate() {
      console.log('Trade-In: Starting cart update...');
      
      try {
        // Get current cart to find the item we just added
        const cartResponse = await fetch('/cart.js');
        const cartData = await cartResponse.json();
        console.log('Trade-In: Cart data:', cartData);

        // Find the trade-in item we just added
        const tradeInItem = cartData.items.find(item => 
          item.properties && item.properties['_discount_code']
        );

        if (tradeInItem) {
          // Trigger a cart/change request to get the Clear-Site-Data header
          // We'll "change" the quantity to the same value - this triggers the cache clear
          console.log('Trade-In: Triggering cart change for cache clear');
          
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: tradeInItem.key,
              quantity: tradeInItem.quantity,
            }),
          });
        }

        // Update cart count store
        if (window.Alpine && window.Alpine.store) {
          const cartCountStore = window.Alpine.store('cart_count');
          if (cartCountStore) {
            console.log('Trade-In: Updating cart_count store');
            cartCountStore.count = cartData.item_count;
            if (cartCountStore.countWithText !== undefined) {
              cartCountStore.countWithText = `${cartData.item_count} items`;
            }
          }
        }

        // Dispatch events that Shapes theme listens for
        document.dispatchEvent(new CustomEvent('baseline:cart:update', {
          detail: { cart: cartData },
          bubbles: true,
        }));

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: cartData,
          bubbles: true,
        }));

      } catch (error) {
        console.error('Trade-In: Failed to update cart', error);
      }

      // Open the cart drawer after cache is cleared
      setTimeout(() => {
        this.openCartDrawer();
      }, 400);
    },

    openCartDrawer() {
      console.log('Trade-In: Opening cart drawer...');
      
      // Shapes theme uses Alpine.js with $store.modals
      if (window.Alpine && window.Alpine.store) {
        const modals = window.Alpine.store('modals');
        
        if (modals) {
          // Try the open method first
          if (typeof modals.open === 'function') {
            console.log('Trade-In: Calling modals.open("cart")');
            modals.open('cart');
            return;
          }
          
          // Alternative: directly set rightDrawer
          if (modals.rightDrawer) {
            console.log('Trade-In: Setting rightDrawer directly');
            modals.rightDrawer.contents = 'cart';
            modals.rightDrawer.open = true;
            return;
          }
        }
      }

      // Fallback: click the cart icon
      const cartIcon = document.querySelector('a[href="/cart"], [href="/cart"]');
      if (cartIcon) {
        cartIcon.click();
      }

      console.log('Trade-In: Cart drawer should be open');
    },

    setConfirmLoading(loading) {
      const textEl = this.elements.confirmBtn.querySelector('.trade-in-confirm-text');
      const loadingEl = this.elements.confirmBtn.querySelector('.trade-in-confirm-loading');
      
      if (loading) {
        textEl.hidden = true;
        loadingEl.hidden = false;
        this.elements.confirmBtn.disabled = true;
      } else {
        textEl.hidden = false;
        loadingEl.hidden = true;
        this.elements.confirmBtn.disabled = !this.elements.termsCheckbox.checked;
      }
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    // Monitor cart and remove discount if trade-in product is removed
    monitorCartForTradeIn() {
      // Check cart on page load
      this.checkCartForTradeInProduct();

      // Listen for cart update events
      document.addEventListener('cart:updated', () => this.checkCartForTradeInProduct());
      document.addEventListener('baseline:cart:update', () => this.checkCartForTradeInProduct());
      
      // Poll cart periodically to catch changes
      setInterval(() => this.checkCartForTradeInProduct(), 3000);

      // Intercept cart change requests
      this.interceptCartChanges();
    },

    interceptCartChanges() {
      // Intercept fetch calls to cart endpoints
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function(...args) {
        const url = args[0];
        const urlString = typeof url === 'string' ? url : url.url || '';

        // Check if this is a cart modification request
        if (urlString.includes('/cart/change') || urlString.includes('/cart/update') || urlString.includes('/cart/clear')) {
          return originalFetch.apply(this, args).then(response => {
            // After cart change, check if trade-in product is still there
            setTimeout(() => self.checkCartForTradeInProduct(), 500);
            return response;
          });
        }

        return originalFetch.apply(this, args);
      };
    },

    async checkCartForTradeInProduct() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();

        // Check if any item has trade-in properties
        const hasTradeInItem = cart.items.some(item => {
          return item.properties && (
            item.properties['_trade_in_brand'] ||
            item.properties['_discount_code']
          );
        });

        // Get the stored discount code from localStorage
        const storedDiscountCode = localStorage.getItem('tradeInDiscountCode');

        if (!hasTradeInItem && storedDiscountCode) {
          console.log('Trade-In: Product removed, clearing discount code');
          await this.removeDiscountCode();
          localStorage.removeItem('tradeInDiscountCode');
        }
      } catch (error) {
        console.error('Trade-In: Error checking cart', error);
      }
    },

    async removeDiscountCode() {
      try {
        // Shopify doesn't have a direct API to remove discount codes
        // The discount code persists in session until checkout or replaced
        
        // Method 1: Try to clear via discount endpoint (may not work)
        await fetch('/discount/', {
          method: 'GET',
          credentials: 'same-origin',
        });

        // Method 2: Clear cart attributes
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              'trade_in_discount': '',
              'discount_code': '',
            },
          }),
        });

        // Note: The discount code may still show in checkout
        // This is a Shopify limitation - discount codes can only be fully
        // removed by completing checkout or starting a new session
        
        console.log('Trade-In: Attempted to clear discount code');
        
        // Refresh the page to potentially clear the discount
        // Uncomment if you want aggressive clearing:
        // window.location.reload();
        
      } catch (error) {
        console.error('Trade-In: Error removing discount', error);
      }
    },

    // Store discount code when created
    storeDiscountCode(code) {
      localStorage.setItem('tradeInDiscountCode', code);
    },
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => TradeIn.init());
  } else {
    TradeIn.init();
  }

  // Expose for debugging
  window.TradeIn = TradeIn;
})();
